const COMPANY_SUFFIX_ALIASES = {
    limited: 'ltd',
    incorporated: 'inc',
    corporation: 'corp',
    company: 'co',
};

const LEGAL_SUFFIX_WORDS = new Set(['ltd', 'llp', 'plc', 'inc', 'corp', 'co', 'lp', 'llc']);
const BROAD_REGION_WORDS = new Set(['europe', 'european']);
const MIN_CORE_WORD_COUNT = 2;
const MAX_CORE_TYPO_DISTANCE = 1;

const normalizeVendorName = (vendorName) => {
    if (vendorName === null || vendorName === undefined) {
        return '';
    }

    return vendorName
        .toString()
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[.'`]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ')
        .split(' ')
        .map((word) => COMPANY_SUFFIX_ALIASES[word] || word)
        .join(' ');
};

const normalizeVendorCoreName = (vendorName) => {
    const normalizedVendorName = normalizeVendorName(vendorName);

    if (!normalizedVendorName) {
        return '';
    }

    const words = normalizedVendorName.split(' ');

    if (words[0] === 'the') {
        words.shift();
    }

    while (words.length > 0 && LEGAL_SUFFIX_WORDS.has(words[words.length - 1])) {
        words.pop();
    }

    const coreWords = words.filter((word) => !BROAD_REGION_WORDS.has(word));

    if (coreWords.length < MIN_CORE_WORD_COUNT || coreWords.some((word) => word.length === 1)) {
        return '';
    }

    return coreWords.join(' ');
};

const isEditDistanceAtMost = (firstValue, secondValue, maxDistance) => {
    if (Math.abs(firstValue.length - secondValue.length) > maxDistance) {
        return false;
    }

    const previousRow = Array.from({ length: secondValue.length + 1 }, (_, index) => index);

    for (let firstIndex = 1; firstIndex <= firstValue.length; firstIndex += 1) {
        const currentRow = [firstIndex];
        let rowMinimum = currentRow[0];

        for (let secondIndex = 1; secondIndex <= secondValue.length; secondIndex += 1) {
            const substitutionCost = firstValue[firstIndex - 1] === secondValue[secondIndex - 1] ? 0 : 1;
            const distance = Math.min(
                previousRow[secondIndex] + 1,
                currentRow[secondIndex - 1] + 1,
                previousRow[secondIndex - 1] + substitutionCost
            );

            currentRow[secondIndex] = distance;
            rowMinimum = Math.min(rowMinimum, distance);
        }

        if (rowMinimum > maxDistance) {
            return false;
        }

        for (let index = 0; index < currentRow.length; index += 1) {
            previousRow[index] = currentRow[index];
        }
    }

    return previousRow[secondValue.length] <= maxDistance;
};

const isLikelySameVendorCoreName = (coreName, existingCoreName) => {
    if (!coreName || !existingCoreName || coreName === existingCoreName) {
        return false;
    }

    const words = coreName.split(' ');
    const existingWords = existingCoreName.split(' ');

    return Boolean(
        words.length === existingWords.length &&
        words.length >= MIN_CORE_WORD_COUNT &&
        words[0] === existingWords[0] &&
        isEditDistanceAtMost(coreName, existingCoreName, MAX_CORE_TYPO_DISTANCE)
    );
};

const findMatchingVendor = (vendors, vendorName, ignoredVendorId = null) => {
    const normalizedVendorName = normalizeVendorName(vendorName);
    const normalizedVendorCoreName = normalizeVendorCoreName(vendorName);

    if (!normalizedVendorName) {
        return null;
    }

    return vendors?.find((vendor) => {
        if (!vendor || vendor.id === ignoredVendorId) {
            return false;
        }

        if (normalizeVendorName(vendor.vendor) === normalizedVendorName) {
            return true;
        }

        const existingVendorCoreName = normalizeVendorCoreName(vendor.vendor);

        if (!normalizedVendorCoreName || !existingVendorCoreName) {
            return false;
        }

        return (
            existingVendorCoreName === normalizedVendorCoreName ||
            isLikelySameVendorCoreName(normalizedVendorCoreName, existingVendorCoreName)
        );
    }) || null;
};

const createDuplicateVendorError = (existingVendor) => {
    const error = new Error(`Vendor already exists as "${existingVendor.vendor}"`);
    error.code = 'DUPLICATE_VENDOR';
    error.existingVendor = existingVendor;
    return error;
};

export { normalizeVendorName, normalizeVendorCoreName, findMatchingVendor, createDuplicateVendorError };
